// ---------- animation engine ----------
// A single requestAnimationFrame loop, which only runs while there's work left: on a
// phone, a permanently running loop at 60fps drains the battery for nothing.
// Everything is in milliseconds on the performance.now() clock.

import { S } from './state';

export const EASE = {
  outCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  outQuint: (t: number) => 1 - Math.pow(1 - t, 5),
  inOut: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  /** clean overshoot: the cell grows past its size then settles */
  outBack: (t: number) => {
    const c = 2.0;
    const u = t - 1;
    return 1 + (c + 1) * u * u * u + c * u * u;
  },
};

// Durations, tuned so the gesture stays snappy: beyond ~450ms, the bloom-in makes the
// game feel sluggish.
export const POP_MS = 340;
const POP_STEP = 14; // offset per cell of distance, hence the wave
const POP_MAX = 260; // maximum offset: on a large zone, we don't wait forever
/** Beyond this many cells, we drop the cell-by-cell bloom-in for a constant-cost radial
 *  wave: 500 canvas transforms per frame go through, 14 000 don't (p90 measured at
 *  37ms, so dropped frames). */
export const POP_CELLS = 500;
export const WAVE_MS = 560;
export const RING_MS = 460;
/** La vie d'une étincelle : elle s'ouvre, brille, se referme. */
export const TWINKLE_MS = 640;
const PART_MS = 780;
const SHAKE_MS = 360;
export const FINAL_MS = 2400;
const PART_GRAV = 900; // px/s² in the grid's space

/** Zone counts: how long the sheet has to be left alone before the count plates fade out of
 *  the way, and how long that fade takes. 2.5s is chosen to be longer than the gap between two
 *  taps and shorter than a pause: stopping for that long IS the player looking at the picture,
 *  which is precisely when the plates are in the way. Erring long on purpose — the numbers are
 *  what the player came to read, and taking them away mid-reading is the worse mistake of the
 *  two. See platesAlpha() in render.ts. */
export const PLATES_IDLE_MS = 2500;
export const PLATES_FADE_MS = 420;

/** Ceilings: tapping very fast stacks up effects, and the frame budget goes with it.
 *  We keep the most recent ones, which are the ones the player is looking at. */
const MAX_PARTS = 220;

/**
 * The replay. A finished sheet refills itself in the order it was actually painted — the saved
 * taps ARE that order (see progress.ts), so there is nothing to record and nothing to invent.
 *
 * Pace: a fixed duration per sheet rather than a fixed speed, so a 1 200-cell puzzle and a
 * 10 000-cell one both play as one gesture. The floor keeps a small sheet from being over
 * before the eye has settled; the ceiling keeps a large one from outstaying its welcome — past
 * eleven seconds you are no longer watching your drawing, you are waiting for a screen.
 *
 * A third slower than the previous pass, all three values together so the shape of the gesture
 * is unchanged (same cutoff: sheets under ~10 000 cells still scale with size, past it they
 * flatten at the ceiling) — even the floor read as rushed.
 */
const REPLAY_MS_PER_CELL = 1.04;
const REPLAY_MIN_MS = 4000;
const REPLAY_MAX_MS = 10400;
/** How long a cell stays warm after it lands, before it has cooled to graphite. */
export const REPLAY_TRAIL_MS = 430;
/**
 * Cells drawn live at once, at most. The trail is the only per-cell cost of the replay, and on
 * a big sheet a full TRAIL_MS of them is over 800 — the budget that made the bloom-in switch to
 * a wave at 500. Capped, the trail simply gets shorter as the sheet gets faster, which is what
 * a comet does anyway.
 */
export const REPLAY_TRAIL_MAX = 280;

/**
 * « La récolte » — the light that runs once over every cell of a symbol that has just run out.
 * See drawHarvest() in render.ts for the drawing, startHarvest() in ui.ts for the beat it sits in.
 *
 * Long enough for the eye to follow a light across the whole sheet, short enough to stay a beat
 * rather than a scene: past a second and a half the player is no longer being rewarded, they are
 * waiting for the game to hand the sheet back. The tail is the last stretch of that time, over
 * which the wash left behind fades out — the shape of what was painted stays readable for a
 * moment instead of being switched off.
 */
export const FLARE_MS = 1500;
/** Share of that time the two fronts take to cross. What is left is the hold — the whole harvest
 *  lit at once, which is the frame the reward actually lives in — and then the fade. */
export const FLARE_CROSS = 0.5;
export const FLARE_TAIL_MS = 350;

/**
 * Ce que devient le cadrage quand la récolte se termine — elle en a emprunté un pour reculer sur
 * la feuille, il faut bien en décider :
 *
 *  - `handback` : la récolte est allée jusqu'au bout. Le zoom du joueur revient, posé sur du
 *    travail à faire (cf. handBack dans ui.ts) ;
 *  - `undo` : un doigt s'est posé AVANT le premier éclat. Rien n'a été montré, donc rien ne s'est
 *    passé : le cadrage emprunté revient tout de suite, sans vol. C'est le seul cas où la caméra
 *    saute, et c'est voulu — la main est déjà sur la feuille, elle la veut maintenant ;
 *  - `keep` : le geste dit lui-même où aller (un zoom, cf. applyZoom), ou la lumière avait déjà
 *    commencé et le joueur agit sur la vue d'ensemble qu'il a sous les yeux. On ne touche à rien.
 *
 * `undo` existe parce que sans lui l'interruption laissait la caméra là où le vol en était : ni le
 * zoom du joueur, ni la feuille entière. Mesuré sur un téléphone, une seconde tape 400 ms après le
 * dernier symbole laissait la feuille à z = 1,35 pour un joueur qui peignait à 5,35 — et sa tape,
 * lue à cette échelle-là, était comprise comme « cadrer une zone » et sautait ailleurs.
 */
export type FlareEnd = 'handback' | 'undo' | 'keep';

export interface Flare {
  code: number;
  /** Date the light starts moving — later than now while the camera pulls back. */
  t0: number;
  /** Cell the fill that emptied the symbol ended on: the light leaves from there, both ways. */
  r0: number;
  c0: number;
  /** Rend vrai si le cadrage a SAUTÉ (le cas `undo`) : l'appelant est alors un doigt qui vient de
   *  se poser, et l'image qu'il visait n'est plus celle qui est à l'écran. */
  end: (how: FlareEnd) => boolean;
}

/** One frame's worth of revealed cells, kept only while they are still cooling. */
export interface ReplayBatch {
  from: number;
  to: number;
  t: number;
}
export interface Replay {
  /** Date the FIRST cell lands. Later than now while the camera pulls back to the whole
   *  sheet: a replay watched through a keyhole shows nothing. */
  t0: number;
  dur: number;
  total: number;
  /** Cells revealed so far — an index into the tap list, which is the whole state. */
  n: number;
  batches: ReplayBatch[];
  end: () => void;
}

/** System "reduced motion" setting: everything here is decorative, never carries
 *  information, so it can all be cut without losing anything of the game. */
export const SYSTEM_REDUCED_MOTION =
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

let motionOff = SYSTEM_REDUCED_MOTION;

/** True when animations are to be skipped. A function and not a constant because the player can
 *  now say so in the settings, and the system preference is only the starting point — someone
 *  who leaves it on system-wide may still want the sheet to bloom. */
export function noMotion(): boolean {
  return motionOff;
}

export function setMotion(enabled: boolean): void {
  motionOff = !enabled;
}

export interface Cell {
  r: number;
  c: number;
  t0: number;
}
export interface Wave {
  t0: number;
  zone: Array<[number, number]>;
  r0: number;
  c0: number;
  dur: number;
  rmax: number;
}
export interface Ring {
  x: number;
  y: number;
  t0: number;
  col: string | null;
}
/** Une étincelle sur une case du symbole qu'on vient de finir (cf. animTwinkles). */
export interface Twinkle {
  x: number;
  y: number;
  t0: number;
  /** Sa taille, en part de la case : elles ne sont pas toutes pareilles, sinon c'est une grille. */
  s: number;
  /** Son angle de départ : elle tourne un peu en s'ouvrant. */
  rot: number;
}
export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  code: number;
  t0: number;
  life: number;
}
export const anim = {
  t: 0,
  last: 0,
  pend: [] as Cell[], // cells currently blooming in
  wave: null as Wave | null, // large zone: radial wave
  mask: null as Uint8Array | null, // 1 = cell drawn live (so absent from the frozen layer)
  until: 0, // end date of the current wave
  rings: [] as Ring[],
  twinkles: [] as Twinkle[],
  parts: [] as Particle[],
  shake: 0,
  final: 0,
  /** Zone counts: date at which they started fading out of the way, 0 while they are up.
   *
   *  A date and not "time since the last touch": the frame clock and the timer that starts the
   *  fade are not the same reading. A rAF timestamp is the frame's own begin time and can land a
   *  hair BEFORE the performance.now() the timer fired at, so a fade expressed as an inequality
   *  between the two gets skipped outright — the woken loop finds nothing to do, goes straight
   *  back to sleep, and the plates stay up for good. Every other effect here carries its own t0
   *  for the same reason. See animPoke() and platesAlpha(). */
  fade: 0,
  /** The zone counts are on screen. Set by rendering every frame: the loop has to know whether
   *  there is a fade left to run without knowing what a zone is. */
  plates: false,
  /** La boussole est en train d'apparaître ou de disparaître. Posé par le rendu à chaque image,
   *  comme `plates`, et pour la même raison : le fondu dure quelques images de plus que le geste
   *  qui l'a déclenché, et sans ça la boucle s'arrête au milieu. */
  guide: false,
  /** Camera flight, started by tapping a zone from the overview. Held here rather than in
   *  render.ts so animActive() knows to keep the loop alive while it plays. */
  view: null as ViewFlight | null,
  /** The sheet is refilling itself, in the order it was painted. Null the rest of the time. */
  replay: null as Replay | null,
  /** A symbol has just run out and its cells are being lit. Null the rest of the time. */
  flare: null as Flare | null,
  /** Le repère du symbole suivant entre en fondu à partir de cette date, 0 le reste du temps —
   *  cf. haloAlpha(). Une date et non une durée écoulée, pour la raison de `fade`. */
  haloIn: 0,
  /** Le repère est retenu, invisible : entre la dernière case d'un symbole et la fin de sa
   *  récolte. Cf. symbolDone dans ui.ts. */
  haloHeld: false,
  running: false,
};

export interface ViewFlight {
  z0: number;
  x0: number;
  y0: number;
  z1: number;
  x1: number;
  y1: number;
  t0: number;
  /** Durée du vol ; VIEW_MS quand rien n'est demandé. */
  ms: number;
  /** Un recul (ou une plongée) de cinéma plutôt qu'un déplacement, cf. FlyOptions. */
  dolly: boolean;
}
/** Long enough to read as travel across the sheet, short enough not to be in the way. */
export const VIEW_MS = 420;

/**
 * Ce qu'un vol peut demander de plus que le déplacement ordinaire.
 *
 * `dolly` : un vol qui change BEAUCOUP le zoom ne peut pas l'interpoler en ligne droite. De 5 à
 * 1 en ligne droite, la moitié du trajet fait passer le zoom de 5 à 3, l'autre de 3 à 1 : la
 * feuille rétrécit d'abord à peine, puis s'effondre — et comme x et y glissent eux aussi en ligne
 * droite, le point regardé dérive en courbe hors du centre. Ici le zoom suit une progression
 * géométrique (chaque instant divise la taille par le même facteur, ce que l'œil lit comme une
 * vitesse constante) et c'est le point du MONDE au centre de l'écran qui glisse en ligne droite,
 * pas l'origine du repère. Lent au départ et à l'arrivée (inOut) : on décolle, puis on se pose.
 */
export interface FlyOptions {
  ms?: number;
  dolly?: boolean;
}

/** Frames the given viewport, over time. Instant when the system asks for less motion. */
export function animFlyTo(z: number, x: number, y: number, opts: FlyOptions = {}): void {
  if (noMotion()) {
    S.viewport.z = z;
    S.viewport.x = x;
    S.viewport.y = y;
    anim.view = null;
    return;
  }
  anim.view = {
    z0: S.viewport.z,
    x0: S.viewport.x,
    y0: S.viewport.y,
    z1: z,
    x1: x,
    y1: y,
    t0: animNow(),
    ms: opts.ms ?? VIEW_MS,
    dolly: opts.dolly ?? false,
  };
  animWake();
}

/** Callbacks provided by rendering: the animation doesn't need to know about layers. */
let onFrame: () => void = () => {};
let onBake: (
  cells: Array<[number, number]>,
  waveZone: Array<[number, number]> | null,
) => void = () => {};
let onWave: () => void = () => {};
let onReveal: (from: number, to: number) => void = () => {};
export function wireAnim(cbs: {
  frame: () => void;
  /** Les cases qui rejoignent la couche figée : celles des petites éclosions, et la zone d'une
   *  vague (un grand remplissage), dont la couche à elle contient déjà les dessins (cf. bakeCells
   *  dans render.ts). */
  bake: (cells: Array<[number, number]>, waveZone: Array<[number, number]> | null) => void;
  wave: () => void;
  reveal: (from: number, to: number) => void;
}): void {
  onFrame = cbs.frame;
  onBake = cbs.bake;
  onWave = cbs.wave;
  onReveal = cbs.reveal;

  // Revenir au premier plan est le moment exact où une image abandonnée manque à l'appel : la
  // boucle se croit vivante, et sans ce réveil elle attendrait le prochain geste du joueur —
  // qui verrait d'abord un écran figé. Le réveil est sans effet si elle tourne vraiment.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) animWake();
  });
}

export function animNow(): number {
  return performance.now();
}

export function animResetGrid(): void {
  anim.pend.length = 0;
  anim.wave = null;
  anim.mask = new Uint8Array(S.gridW * S.gridH);
  anim.until = 0;
  anim.rings.length = anim.parts.length = anim.twinkles.length = 0;
  anim.shake = anim.final = 0;
  anim.haloHeld = false;
  anim.haloIn = 0;
  // A replay left running would keep revealing taps that belong to the puzzle just closed —
  // into a grid that is no longer the same size. Dropped, not finished: its `end` celebrates,
  // and there is nothing to celebrate about leaving.
  anim.replay = null;
  // Dropped for the same reason, and its `end` skipped rather than run: it hands back a viewport
  // measured on the sheet being left, and whatever comes next sets its own.
  anim.flare = null;
}

/**
 * Arms the harvest: `delay` holds the light back while the camera pulls out, exactly as the
 * replay's does, and `end` runs once it has crossed.
 */
export function animFlare(
  code: number,
  r0: number,
  c0: number,
  delay: number,
  end: (how: FlareEnd) => boolean,
): void {
  // A second symbol emptied before the first one's light has crossed — two chords, one light, and
  // it is the one that just happened. Its predecessor is ended without restoring, since the
  // viewport to give back is the one this new beat is about to take over.
  if (anim.flare) animFlareEnd('keep');
  anim.flare = { code, t0: animNow() + delay, r0, c0, end };
  animWake();
}

/**
 * La récolte a-t-elle commencé à éclairer ?
 *
 * Faux pendant le recul de la caméra : la lumière attend que le cadrage soit en place (cf. `t0`
 * et le `q < 0` de drawHarvest), et pendant ce temps le joueur n'a RIEN vu. C'est toute la
 * différence entre les deux façons de l'interrompre — voir FlareEnd.
 */
export function flareLit(): boolean {
  const fl = anim.flare;
  return !!fl && animNow() >= fl.t0;
}

/**
 * Ends the harvest early — a hand back on the sheet, a zoom. Voir FlareEnd pour `how`.
 *
 * Rend vrai quand le cadrage a sauté d'un coup, ce que seul `undo` fait : l'appelant a besoin de
 * le savoir pour ne pas laisser le geste en cours agir sur des coordonnées devenues fausses. Vrai
 * n'est PAS déductible de `how` — une récolte qui n'avait pas eu à reculer n'a rien à défaire.
 */
export function animFlareEnd(how: FlareEnd): boolean {
  const fl = anim.flare;
  if (!fl) return false;
  // Cleared BEFORE the callback: `end` moves the camera and redraws, and a flare still standing
  // would have that frame build the region it is about to drop.
  anim.flare = null;
  // The player has taken the sheet back, so the camera motion the beat owned stops here. Without
  // this, a flare cut during its own pull-back leaves that flight running, and it keeps writing
  // the viewport for up to VIEW_MS — over the zoom or the framing just asked for. Safe to drop
  // unconditionally: the only flight that can be in the air during a harvest is its own, since
  // every gesture that starts one cancels the harvest first.
  //
  // `handback` est le seul cas qui l'épargne, et il n'a rien à épargner : à ce moment-là le vol
  // du recul est fini depuis longtemps, et c'est le rappel du cadrage qui va en lancer un.
  if (how !== 'handback') anim.view = null;
  return fl.end(how);
}

/**
 * Starts a replay of `total` taps. `delay` holds the first cell back while the camera pulls
 * out; `end` runs once the last one has landed — and only then, so the ending's own sequence
 * never overlaps the drawing being drawn.
 */
export function animReplay(total: number, delay: number, end: () => void): void {
  const dur = Math.max(REPLAY_MIN_MS, Math.min(REPLAY_MAX_MS, total * REPLAY_MS_PER_CELL));
  anim.replay = { t0: animNow() + delay, dur, total, n: 0, batches: [], end };
  animWake();
}

/**
 * Drops the replay because the player has left the sheet — the catalogue is opening over it.
 * The cells still to reveal are revealed at once, so the sheet is whole and the save's own
 * picture of it stays true; `end` is NOT run, since it celebrates, and nobody is watching.
 *
 * Without this, a puzzle finished and left in the same breath would have its card come up over
 * the catalogue a few seconds later, in front of a shelf of other puzzles.
 */
export function animReplayStop(): void {
  const rp = anim.replay;
  if (!rp) return;
  anim.replay = null;
  if (rp.n < rp.total) onReveal(rp.n, rp.total);
}

/** Cuts the replay short — a tap on the sheet — and lands on its last frame rather than
 *  abandoning it: the drawing must end up whole either way. */
export function animReplaySkip(): void {
  const rp = anim.replay;
  if (!rp) return;
  // Twice over, and measured from now: a frame is timestamped a hair before the call that
  // asked for it, and a replay left one frame short of its end would never finish.
  rp.t0 = animNow() - rp.dur * 2;
  animWake();
}

/**
 * Wave bloom-in: the delay grows with the distance to the touched point, which makes
 * the fill run outward from the finger instead of lighting up as one block.
 */
export function animPop(zone: Array<[number, number]>, r0: number, c0: number): void {
  if (noMotion()) {
    onBake(zone, null);
    return;
  } // the cells are already filled: they show up all at once
  const t = animNow();
  // In both cases the cells leave the frozen layer: they're drawn live.
  if (anim.mask) for (const [r, c] of zone) anim.mask[r * S.gridW + c] = 1;

  if (zone.length > POP_CELLS) {
    anim.wave = { t0: t, zone, r0, c0, dur: WAVE_MS, rmax: 0 };
    onWave();
    anim.until = t + WAVE_MS;
    animWake();
    return;
  }
  let until = t + POP_MS;
  for (const [r, c] of zone) {
    const d = Math.hypot(r - r0, c - c0);
    const t0 = t + Math.min(POP_MAX, d * POP_STEP);
    anim.pend.push({ r, c, t0 });
    if (t0 + POP_MS > until) until = t0 + POP_MS;
  }
  anim.until = until;
  animWake();
}

/** 0..1 progress of a cell blooming in, or -1 if it hasn't started yet. */
export function animPhase(t0: number): number {
  if (anim.t < t0) return -1;
  return Math.min(1, (anim.t - t0) / POP_MS);
}

export function animRing(x: number, y: number, col?: string): void {
  if (noMotion()) return;
  anim.rings.push({ x, y, t0: animNow(), col: col ?? null });
  animWake();
}

export function animTwinkles(list: Twinkle[]): void {
  if (noMotion() || !list.length) return;
  anim.twinkles.push(...list);
  animWake();
}

export function animBurst(x: number, y: number, n: number, code: number, spread = 2.4): void {
  if (noMotion()) return;
  if (anim.parts.length > MAX_PARTS) anim.parts.splice(0, anim.parts.length - MAX_PARTS);
  const t = animNow();
  for (let i = 0; i < n; i++) {
    const a = -Math.PI / 2 + (Math.random() - 0.5) * spread;
    const v = 160 + Math.random() * 260;
    anim.parts.push({
      x,
      y,
      vx: Math.cos(a) * v,
      vy: Math.sin(a) * v,
      rot: Math.random() * 6.28,
      vr: (Math.random() - 0.5) * 9,
      code,
      t0: t,
      life: PART_MS * (0.7 + Math.random() * 0.6),
    });
  }
  animWake();
}

export function animShake(): void {
  if (noMotion()) return;
  anim.shake = animNow();
  animWake();
}

/** The frame stays off via S.gameOver, so cutting the sequence doesn't leave the
 *  drawing in a half-finished state. */
export function animFinal(): void {
  if (noMotion()) {
    onFrame();
    return;
  }
  anim.final = animNow();
  animWake();
}

/** Shake offset: damped sine wave, in screen pixels. */
export function animShakeOffset(): { x: number; y: number } | null {
  if (!anim.shake) return null;
  const p = (anim.t - anim.shake) / SHAKE_MS;
  if (p >= 1) {
    anim.shake = 0;
    return null;
  }
  const a = 7 * (1 - p) * (1 - p);
  return { x: Math.sin(p * 38) * a, y: Math.cos(p * 29) * a * 0.45 };
}

function animStep(): void {
  const dt = Math.min(0.032, (anim.t - anim.last) / 1000) || 0.016;
  anim.last = anim.t;

  for (let i = anim.parts.length - 1; i >= 0; i--) {
    const p = anim.parts[i];
    if (anim.t - p.t0 >= p.life) {
      anim.parts.splice(i, 1);
      continue;
    }
    p.vy += PART_GRAV * dt;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.rot += p.vr * dt;
    p.vx *= 0.99;
  }
  for (let i = anim.rings.length - 1; i >= 0; i--) {
    if (anim.t - anim.rings[i].t0 >= RING_MS) anim.rings.splice(i, 1);
  }
  if (anim.twinkles.length) anim.twinkles = anim.twinkles.filter((w) => anim.t - w.t0 < TWINKLE_MS);
  // End of wave: the cells rejoin the frozen layer, in a single recompute rather than
  // one per settling cell.
  if ((anim.pend.length || anim.wave) && anim.t >= anim.until) {
    const cells: Array<[number, number]> = anim.pend.map((p) => [p.r, p.c]);
    const waveZone = anim.wave?.zone ?? null;
    anim.pend.length = 0;
    anim.wave = null;
    anim.mask?.fill(0);
    onBake(cells, waveZone);
  }
  if (anim.final && anim.t - anim.final >= FINAL_MS) anim.final = 0;

  const rp = anim.replay;
  if (rp && anim.t >= rp.t0) {
    const p = Math.min(1, (anim.t - rp.t0) / rp.dur);
    // Eased over the whole run, not linear: the hand starts, gets going, and sets down. A
    // constant rate reads as a progress bar, which is the one thing this is not.
    const k = Math.round(EASE.inOut(p) * rp.total);
    if (k > rp.n) {
      // The reveal is the caller's: it owns the sheet's state and the note that goes with it.
      onReveal(rp.n, k);
      rp.batches.push({ from: rp.n, to: k, t: anim.t });
      rp.n = k;
    }
    while (rp.batches.length && anim.t - rp.batches[0].t > REPLAY_TRAIL_MS) rp.batches.shift();
    if (p >= 1) {
      // Cleared BEFORE the callback: `end` starts the ending, which asks the loop for frames,
      // and a replay still standing would hand it cells it has already revealed.
      const end = rp.end;
      anim.replay = null;
      end();
    }
  }

  const fl = anim.flare;
  if (fl && anim.t - fl.t0 >= FLARE_MS) animFlareEnd('handback');

  const v = anim.view;
  if (v) {
    // Borné DES DEUX CÔTÉS, et le zéro n'est pas de la prudence décorative : il a coûté un bug.
    //
    // `t0` est posé par animFlyTo() avec performance.now(), tandis que `anim.t` est l'horodatage
    // que requestAnimationFrame donne à l'image — c'est-à-dire l'instant du vsync, qui est
    // LÉGÈREMENT DANS LE PASSÉ. Sur un ordinateur l'écart se voit à peine ; sur le Pixel 7a il
    // atteint la poignée de millisecondes, et la première image d'un vol arrivait alors avec
    // p ≈ -0,016. outCubic vaut 1-(1-p)³, donc k passait à -0,049 — et un vol de z=1 vers z=24
    // posait un zoom de -0,13. Le zoom NÉGATIF donnait ensuite un rayon négatif à roundRect dans
    // paintPaper (mesuré : -35,9), qui lève, ce qui tuait l'image en cours : le canevas restait
    // effacé, et jusqu'au garde-fou de la boucle il fallait quitter l'application.
    const p = Math.min(1, Math.max(0, (anim.t - v.t0) / v.ms));
    if (v.dolly) {
      // Cf. FlyOptions : le zoom en progression géométrique, et le point du monde au centre de
      // l'écran en ligne droite. Les deux zooms sont positifs (ZOOM_MIN > 0), le rapport aussi.
      const k = EASE.inOut(p);
      const z = v.z0 * Math.pow(v.z1 / v.z0, k);
      const cx0 = (S.availW / 2 - v.x0) / v.z0;
      const cy0 = (S.availH / 2 - v.y0) / v.z0;
      const cx = cx0 + ((S.availW / 2 - v.x1) / v.z1 - cx0) * k;
      const cy = cy0 + ((S.availH / 2 - v.y1) / v.z1 - cy0) * k;
      S.viewport.z = z;
      S.viewport.x = S.availW / 2 - cx * z;
      S.viewport.y = S.availH / 2 - cy * z;
    } else {
      const k = EASE.outCubic(p); // decelerates on arrival, like releasing a pinch
      S.viewport.z = v.z0 + (v.z1 - v.z0) * k;
      S.viewport.x = v.x0 + (v.x1 - v.x0) * k;
      S.viewport.y = v.y0 + (v.y1 - v.y0) * k;
    }
    if (p >= 1) anim.view = null;
  }
}

/** The count plates have a fade in progress. Nothing at all during the idle WAIT that precedes
 *  it — waiting is not animating, and 2.5s of identical frames is the exact thing this engine
 *  exists not to do: the wait is held by animPoke's timer, which starts the fade and wakes the
 *  loop for it. Under reduced motion there is no fade to run, just the jump the timer's own
 *  frame draws. */
function platesFading(): boolean {
  if (!anim.plates || !anim.fade || noMotion()) return false;
  return anim.t - anim.fade < PLATES_FADE_MS;
}

/**
 * Le repère du symbole qui prend la suite d'un palier se lève en fondu (2026-10-31).
 *
 * Il surgissait d'un coup, au moment même où la gerbe éclatait : le verrou passe au symbole
 * suivant dans l'image du tap (cf. onCodeProgress), et des dizaines de cases hachurées
 * s'allumaient en même temps que la récompense — « ça clignote vers les futures cases », dit
 * Vincent. Le fondu laisse la gerbe seule un instant, puis montre où l'on repart.
 */
export const HALO_IN_MS = 650;

export function haloAlpha(): number {
  if (anim.haloHeld) return 0;
  if (!anim.haloIn || noMotion()) return 1;
  const q = (anim.t - anim.haloIn) / HALO_IN_MS;
  return q <= 0 ? 0 : q >= 1 ? 1 : EASE.inOut(q);
}

function haloFading(): boolean {
  return anim.haloIn > 0 && !noMotion() && anim.t - anim.haloIn < HALO_IN_MS;
}

function animActive(): boolean {
  return (
    anim.pend.length > 0 ||
    anim.wave !== null ||
    anim.parts.length > 0 ||
    anim.rings.length > 0 ||
    anim.twinkles.length > 0 ||
    anim.shake > 0 ||
    anim.final > 0 ||
    anim.view !== null ||
    anim.replay !== null ||
    anim.flare !== null ||
    anim.guide ||
    platesFading() ||
    haloFading()
  );
}

/**
 * Quand la dernière image a réellement été dessinée, et l'identité de la boucle qui l'a dessinée.
 *
 * `anim.running` ne dit pas « la boucle tourne », il dit « quelqu'un a demandé une image » — et
 * c'est une croyance, pas un fait. Deux choses la démentent, et les deux se sont produites sur
 * un téléphone pendant une récolte, la plus longue animation du jeu :
 *
 *  - **une image qui n'arrive jamais.** Une WebView qui gèle, l'appli qui passe en arrière-plan,
 *    et le rappel de `requestAnimationFrame` est simplement abandonné ;
 *  - **une exception dans l'image.** La ligne qui replanifie la suivante est la dernière du
 *    corps : si quoi que ce soit lève avant, elle n'est pas atteinte.
 *
 * Dans les deux cas `running` restait vrai pour toujours, donc `animWake()` — qui commençait par
 * `if (anim.running) return` — ne redémarrait plus jamais rien. Plus une seule animation, et si
 * l'interruption tombait au milieu d'un rendu, une feuille effacée qui ne se redessinait pas :
 * il fallait quitter l'application. D'où le témoin de vie ci-dessous, et le try/catch.
 */
let lastFrameAt = 0;
let loopId = 0;

/** Sans image depuis ce délai alors que la boucle se dit vivante, elle est morte. Large devant
 *  une image (16 ms) et devant une pile de plusieurs images en retard, court devant le temps
 *  qu'il faut à un joueur pour s'apercevoir que l'écran ne bouge plus. */
const STALL_MS = 400;

function startLoop(): void {
  // Une génération par boucle : si un réveil en relance une alors qu'une image de l'ancienne
  // finit par arriver, l'ancienne se retire au lieu de dessiner en double.
  const id = ++loopId;
  const step = (ts: number): void => {
    if (id !== loopId) return;
    lastFrameAt = animNow();
    anim.t = ts;
    try {
      animStep();
      onFrame();
    } catch (err) {
      // L'erreur part dans la console, où elle reste trouvable — l'avaler en silence ferait
      // d'un bug un mystère. Sur l'APK elle atterrit dans logcat, préfixée `animFrame`.
      anim.running = false;
      console.error('animFrame', err);
      // Et surtout, on ne laisse pas la feuille dans l'état où l'exception l'a surprise :
      // le rendu EFFACE avant de peindre, donc une image interrompue en son milieu laisse un
      // canevas vide, et il le restait jusqu'au prochain geste du joueur — plusieurs secondes
      // de page blanche pour une image ratée. On abandonne les effets en cours, qui sont
      // l'endroit où le risque se prend, et on redemande une image propre tout de suite.
      anim.flare = null;
      // La récolte abandonnée sans son `end` : le repère qu'elle retenait (cf. symbolDone) ne
      // serait jamais rendu, et avec lui la boussole et les compteurs de zones.
      anim.haloHeld = false;
      anim.view = null;
      anim.wave = null;
      anim.parts.length = 0;
      anim.rings.length = 0;
      anim.twinkles.length = 0;
      anim.shake = 0;
      try {
        onFrame();
      } catch {
        /* la feuille est perdue pour cette image, l'application ne l'est pas */
      }
      return;
    }
    if (animActive()) requestAnimationFrame(step);
    else {
      anim.running = false;
      onFrame(); // one last clean frame, no effects
    }
  };
  anim.running = true;
  anim.t = anim.last = lastFrameAt = animNow();
  requestAnimationFrame(step);
}

export function animWake(): void {
  // La boucle tourne ET elle a donné signe de vie récemment : rien à faire.
  if (anim.running && animNow() - lastFrameAt < STALL_MS) return;
  startLoop();
}

let idleTimer = 0;

/**
 * "The player just did something to the sheet." Restarts the countdown the zone counts fade on,
 * and hands the fade its frames when the countdown runs out — on a timer rather than by keeping
 * the loop alive through the wait.
 *
 * Called on every pointer event and every zoom, so it has to stay this cheap: one timer
 * rescheduled. The plates normally come back on the render the caller was going to do anyway,
 * `fade` being cleared here — the one frame asked for at the end is for the events that repaint
 * nothing on their own (a mouse crossing the sheet with a symbol locked), and only when there is
 * something faded to bring back.
 */
export function animPoke(): void {
  const faded = anim.fade !== 0;
  anim.fade = 0;
  clearTimeout(idleTimer);
  // The timer is armed even with no plates on screen, and asks the question again when it fires:
  // whether they are showing depends on the zoom the render AFTER this call settles on, so
  // deciding here would be deciding one frame too early.
  idleTimer = setTimeout(() => {
    if (!anim.plates) return;
    anim.fade = animNow();
    animWake();
  }, PLATES_IDLE_MS) as unknown as number;
  if (faded) animWake();
}
